import Vue from 'vue'
import Vuex from 'vuex'
import {
  getContent,
  getSystems,
  getConstructionBySystemId,
  getRequiredIndex,
  getCalculationResult,
  getOptionalSystem, getPdf, getDocument, imageURL
} from '@/api'
import { sendMetrics } from '@/api/metrics'
import { saveAs } from 'file-saver'
import { v4 as uuidv4 } from 'uuid'
import { stepNumbers } from '@/utils/stepNumbers'
import _ from 'lodash'
import { CalcTypes, ConstructionTypes } from '@/utils/types'
import {
  getReportStructure,
  getRequestCalcDataRPO,
  getSectorConstructionTypeId, isolationMaterials,
  wasDataForRequestActual
} from '@/utils/utils'

export function initStore(plugins) {
  const { i18n } = plugins
  const defaultTitle = i18n.messages[i18n.locale].message.store.sector
  const exportTitle = i18n.messages[i18n.locale].message.store.exportTitle

  let metricObject = null

  Vue.use(Vuex)
  const store = new Vuex.Store({
    state: {
      hash: null,
      TNTokenStatus: false,
      TNToken: null,
      isErrorLink: false,
      category: null,
      objectInfo: {
        link: '',
        result: null
      },
      pages: {
        wizard: {
          isLoading: true
        },
        result: {
          isLoading: false
        }
      },
      modal: {
        selectedSystem: null
      },
      activeSector: 0,
      sectors: [
        {
          id: null,
          name: `${defaultTitle} 1`,
          currentStep: 0,
          constructionType: {
            type: ConstructionTypes.wall,
            isCombinedLocation: false,
            buildingType: 'Жилые здания',
            location: null,
            rating: null
          },
          base: {
            base: null,
            density: null,
            thickness: null
          },
          screed: {
            screed: null,
            thickness: null
          },
          facing: {
            facing: null,
            density: null,
            thickness: null,
            sheets: null
          },
          facingInnerLayer: {
            facingInnerLayer: null,
            density: null,
            thickness: null,
            facingInnerLayerBlocked: false
          },
          isolation: {
            isolation: null,
            thickness: null
          },
          soundIsolation: {
            soundIsolation: null,
            thickness: null
          },
          sizes: {
            x: null,
            y: null,
            sizesBlocked: false
          },
          indexes: {
            required: null,
            calculated: null,
            rwDown: null,
            isTested: false
          },
          system: null,
          algorithm: CalcTypes.lite,
          image: null,
          optionalSystems: null,
          construction: null,
          structures: null,
          filling: null,
          isCalculated: false,
          isValid: false,
          errors: null,
          chart: null,
          totalThickness: null,
          roomSystems: null
        }
      ],
      constructionTypes: [],
      additionalConstructionTypeParams: null,
      defaultSector: {
        id: null,
        name: `${defaultTitle}`,
        currentStep: 0,
        constructionType: {
          type: ConstructionTypes.wall,
          buildingType: 'Жилые здания',
          isCombinedLocation: false,
          location: null,
          rating: null
        },
        base: {
          base: null,
          density: null,
          thickness: null
        },
        screed: {
          screed: null,
          thickness: null
        },
        facing: {
          facing: null,
          density: null,
          thickness: null,
          sheets: null
        },
        isolation: {
          isolation: null,
          thickness: null
        },
        soundIsolation: {
          soundIsolation: null,
          thickness: null
        },
        sizes: {
          length: null,
          width: null,
          height: null,
          sizesBlocked: false
        },
        facingInnerLayer: {
          facingInnerLayer: null,
          density: null,
          thickness: null,
          facingInnerLayerBlocked: false
        },
        indexes: {
          required: null,
          calculated: null,
          rwDown: null,
          isTested: false
        },
        system: null,
        algorithm: CalcTypes.lite,
        image: null,
        optionalSystems: null,
        construction: null,
        structures: null,
        filling: null,
        isCalculated: false,
        isValid: false,
        errors: null,
        chart: null,
        totalThickness: null,
        roomSystems: null
      },
      isolationMaterials: {
        categories: [
          {
            value: isolationMaterials.stonewall.value,
            id: isolationMaterials.stonewall.value,
            name: isolationMaterials.stonewall.name
          },
          {
            value: isolationMaterials.fiberglass.value,
            id: isolationMaterials.fiberglass.value,
            name: isolationMaterials.fiberglass.name
          }
        ],
        selected: null,
        preSelected: false
      }
    },
    getters: {
      getTNToken: (state) => state.TNToken,
      getWizardLoadingStatus: (state) => state.pages.wizard.isLoading,
      getSectors: (state) => () => state.sectors,
      getSector: (state) => (index) => state.sectors[index],
      getConstructionTypes: (state) => state.constructionTypes,
      getIsolationTypes: (state) => state.isolationMaterials.categories,
      getSelectedIsolationType: (state) => state.isolationMaterials.selected,
      getIsolationPreSelected: (state) => state.isolationMaterials.preSelected,
      getRooms: (state) => state.additionalConstructionTypeParams,
      getModalValues: (state) => state.modal,
      sizesGetter: (state) => (index) => state.sectors[index].sizes,
      facingInnerLayerGetter: (state) => (index) => state.sectors[index].facingInnerLayer
    },
    mutations: {
      UPDATE_CONTENT(state, constructionTypes) {
        state.constructionTypes = Object.freeze(constructionTypes)
        state.pages.wizard.isLoading = false
      },
      UPDATE_SYSTEM_LIST(state, payload) {
        const constructionTypes = state.constructionTypes.map((type) => {
          payload.forEach((item) => {
            if (item.constructionType === type.constructionType) {
              type.systems = item.systems.map((system) => {
                return {
                  ...system,
                  image: system.image ? `${imageURL}/uploads/${system.image}` : null
                }
              }).sort((a, b) => a.orderAs - b.orderAs)
            }
          })
          return { ...type }
        })
        state.constructionTypes = Object.freeze(constructionTypes)
      },
      UPDATE_ADDITIONAL_PARAMS_LIST(state, payload) {
        if (!state.additionalConstructionTypeParams) {
          state.additionalConstructionTypeParams = {}
        }

        state.additionalConstructionTypeParams[payload.type] = payload.data
          .map((item) => item.buildings)
          .flat()
      },
      UPDATE_TN_TOKEN_STATUS: (state, TNTokenStatus) => {
        state.TNTokenStatus = TNTokenStatus
      },
      UPDATE_TN_TOKEN: (state, token) => {
        state.TNToken = token
      },
      UPDATE_SECTOR: (state, payload) => {
        if (payload.subproperty) {
          state.sectors[payload.index][payload.property] = {
            ...state.sectors[payload.index][payload.property],
            [payload.subproperty]: payload.value
          }

          return
        }

        state.sectors[payload.index][payload.property] = payload.value
      },
      DELETE_SECTOR: (state, payload) => {
        state.sectors.splice(payload.index, 1)
      },
      ADD_SECTORS: (state) => {
        const newSector = { ...state.defaultSector }
        newSector.id = uuidv4()
        newSector.name = `${defaultTitle} ${state.sectors.length + 1}`
        state.sectors.push(newSector)
      },
      UPDATE_ACTIVE_SECTOR: (state, payload) => {
        state.activeSector = payload
      },
      UPDATE_MODAL: (state, payload) => {
        state.modal[payload.property] = payload.value
      },
      UPDATE_ISOLATION_MATERIALS: (state, payload) => {
        state.isolationMaterials.selected = payload
      },
      UPDATE_ISOLATION_MATERIALS_SHOW: (state, payload) => {
        state.isolationMaterials.preSelected = payload
      }
    },
    actions: {
      getContent({ commit, state }) {
        getContent()
          .then((response) => {
            if (state.sectors[0].id === null) state.sectors[0].id = uuidv4()

            commit('UPDATE_CONTENT', response.data)

            return getSystems(state.isolationMaterials.preSelected ? state.isolationMaterials.selected : null)
          })
          .then((response) => {
            commit('UPDATE_SYSTEM_LIST', response.data)
          })
      },
      getSystemForRooms({ state, commit }, index) {
        return getSystems(state.sectors[index].constructionType?.location?.id).then((response) => {
          const roomSystems = response.data ? response.data[0].systems : null
          if (roomSystems) {
            roomSystems.forEach((item) => {
              item.image = item.image ? `${imageURL}/uploads/${item.image}` : null
            })
          }
          commit('UPDATE_SECTOR', { index, property: 'roomSystems', subproperty: null, value: roomSystems })
        })
      },
      getConstructionBySystemId({ commit, state }, index) {
        const systemId = state.sectors[index].system?.id

        getConstructionBySystemId(
          systemId,
          state.sectors[index].algorithm
        )
          .then((response) => {
            commit('UPDATE_SECTOR', { index, property: 'structures', value: response.data })
          })
      },
      getRequiredIndexes({ state, commit }, index) {
        const sector = state.sectors[index]
        const data = {
          locationId: sector.constructionType.location.id,
          rating: sector.constructionType.rating,
          calcType: sector.algorithm,
          isCombinedLocation: sector.constructionType.isCombinedLocation
        }
        getRequiredIndex(data).then((response) => {
          if (
            sector.algorithm === CalcTypes.lite &&
            getSectorConstructionTypeId(state.constructionTypes, state.sectors[index]) === ConstructionTypes.floor &&
            !sector.system.isPartition
          ) {
            response.data.rw = null
          }

          commit('UPDATE_SECTOR', { index, property: 'indexes', subproperty: 'required', value: response.data })
        })
      },
      getOptionalSystem({ state, commit }, index, isException = false) {
        const sector = state.sectors[index]
        const withFilling = Boolean(sector.filling)

        const data = {
          systemId: withFilling ? sector.filling?.id : sector.system?.id,
          materialType: state.isolationMaterials.selected,
          structure: {
            id: isException ? null : sector.base.base?.id,
            density: isException ? null : sector.base.density,
            thickness: isException ? null : sector.base.thickness
          },
          screed: {
            id: sector.screed.screed ? sector.screed.screed.id : null,
            density: null,
            thickness: sector.screed?.thickness ? sector.screed?.thickness : null
          },
          location: {
            locationId: sector.constructionType.location?.id,
            rating: sector.constructionType?.rating,
            isCombinedLocation: sector.constructionType.isCombinedLocation
          }
        }

        getOptionalSystem(data).then((response) => {
          if (response.data) {
            const hasErrors = response.data.hasOwnProperty('rw')
            const property = hasErrors ? 'errors' : 'optionalSystems'
            const value = hasErrors
              ? {
                rw: { text: response.data.rw, needRender: Boolean(response.data.rw) },
                lnw: { text: response.data.lnw, needRender: Boolean(response.data.lnw) }
              }
              : response.data.map((construction) => {
                return {
                  ...construction,
                  materialType: state.isolationMaterials.selected,
                  image: construction.image ? `${imageURL}/uploads/${construction.image}` : null,
                  materials: construction.totalThickness
                    ? [ { title: 'Толщина конструкции', number: 0, thickness: construction.totalThickness }, ...construction.materials ]
                    : construction.materials
                }
              })

            commit('UPDATE_SECTOR', { index, property, value })
          }
        })
      },
      getCalculationResult({ state, commit }, payload) {
        const { index, type, isWithSizes } = payload
        const sector = state.sectors[index]
        const data = getRequestCalcDataRPO(sector, type, isWithSizes)

        const handleError = (error) => {
          const { text, infos } = error
          const { indexes } = infos
          const calculated = indexes ? { rw: indexes?.rw, lnw: indexes?.lnw ?? null } : null
          const rwDown = indexes ? indexes?.rwDown : null

          commit('UPDATE_SECTOR', { index, property: 'errors', value: { rw: { needRender: true, text } } })
          commit('UPDATE_SECTOR', { index, property: 'indexes', subproperty: 'calculated', value: calculated })
          commit('UPDATE_SECTOR', { index, property: 'indexes', subproperty: 'rwDown', value: rwDown })
          commit('UPDATE_SECTOR', { index, property: 'indexes', subproperty: 'isTested', value: null })
          commit('UPDATE_SECTOR', { index, property: 'isCalculated', value: false })
        }

        const handleResponse = (response) => {
          const constructionType = state.sectors[index].constructionType.type
          const wasDataActual = wasDataForRequestActual(data, sector, constructionType, isWithSizes)

          function getCalculatedRw() {
            if (!response) {
              return null
            }

            if (response?.isTested === undefined) {
              return response
            }

            commit('UPDATE_SECTOR', { index, property: 'indexes', subproperty: 'isTested', value: response.isTested })

            return response.rw
          }

          commit('UPDATE_SECTOR', {
            index,
            property: 'indexes',
            subproperty: 'calculated',
            value: { rw: getCalculatedRw(), lnw: response.lnw ?? null }
          })
          commit('UPDATE_SECTOR', {
            index,
            property: 'indexes',
            subproperty: 'rwDown',
            value: isWithSizes ? response?.rwDown : null
          })
          commit('UPDATE_SECTOR', { index, property: 'errors', value: null })
          commit('UPDATE_SECTOR', { index, property: 'isCalculated', value: wasDataActual })

          if ((isWithSizes) && response?.optionSystem) {
            commit('UPDATE_SECTOR', { index,
              property: 'construction',
              value: {
                ...response?.optionSystem,
                image: response?.optionSystem.image ? `${imageURL}/uploads/${response?.optionSystem.image}` : null
              } })
          }

          if (response.chart !== undefined) {
            const totalThickness = response.optionSystem && response.optionSystem?.totalThickness
              ? response.optionSystem.totalThickness
              : { value: response.totalThickness, unitType: i18n.messages[i18n.locale].message.units.mm }
            commit('UPDATE_SECTOR', { index, property: 'chart', value: response.chart })
            commit('UPDATE_SECTOR', { index, property: 'totalThickness', value: totalThickness })
          }
        }

        getCalculationResult(data, type)
          .then((result) => {
            typeof result.data === 'string' || !result || result.data?.type === 'error'
              ? handleError(result.data?.type === 'error' ? result.data : result.data)
              : handleResponse(result.data)
          })
          .catch(() => {
            handleError(null)
          })
      },
      getExportDocument({ state, commit }, payload) {
        function getRequest(data) {
          return payload.action === 'pdf' ? getPdf(data) : getDocument(data)
        }

        const requestData = {
          person: null,
          reports: state.sectors
            .filter(sector => sector.isCalculated)
            .map(sector => getReportStructure(sector))
        }

        getRequest(requestData).then((response) => {
          const file = new Blob([response.data], { type: `application/${payload.action}` })
          const fileName = `${exportTitle}.${payload.action}`
          saveAs(file, fileName)

          if (payload.action === 'pdf') {
            const fileURL = window.URL.createObjectURL(file)
            window.open(fileURL)
          }
        })
      },
      setCalculatedIndexes({ state, commit }, { index, payload }) {
        commit('UPDATE_SECTOR', { index, property: 'indexes', subproperty: 'calculated', value: payload })
      },
      setTestingStatus({ state, commit }, { index, payload }) {
        commit('UPDATE_SECTOR', { index, property: 'indexes', subproperty: 'isTested', value: payload })
      },
      async sendMetrics({ state }) {
        const data = state.sectors
          .filter((sector, index) => state.activeSector === index)
          .map((sector) => {
            return {
              id: sector.id,
              stepNumber: sector.currentStep?.toString(),
              titleSection: sector.name,
              locationInfo: {
                locationId: sector.constructionType.location?.id ?? null,
                rating: sector.constructionType.rating,
                isCombinedLocation: sector.constructionType.isCombinedLocation
              },
              systemId: sector.system?.id ?? null,
              structure: {
                density: sector.base.density ?? 0,
                structureId: sector.base.base?.id ?? null,
                thickness: sector.base.thickness ?? 0
              },
              optionSystem: {
                lnw: sector.construction?.lnw ?? null,
                optionSystemId: sector?.construction?.id ?? null,
                rw: sector.construction?.rw ?? sector?.indexes?.calculated?.rw ?? null
              },
              screed: {
                screedId: sector.screed?.screed?.id ?? null,
                thickness: sector.screed?.thickness
              },
              cladding: {
                claddingId: sector.facing?.facing?.id ?? null,
                density: sector.facing?.density,
                thickness: sector.facing?.thickness,
                qtySheet: sector.facing?.sheets
              },
              sound: {
                soundId: sector.soundIsolation?.soundIsolation?.id ?? sector.isolation?.isolation?.id ?? null,
                thickness: sector.soundIsolation?.thickness ?? sector.isolation?.thickness?.value ?? sector.isolation?.thickness ?? null
              },
              calcType: sector.algorithm
            }
          })

        const obj = { ...data[0], stepNumber: null }
        const idActiveSector = state.sectors[state.activeSector].id
        const sector = state.sectors[state.activeSector]

        if (metricObject === null) {
          metricObject = {}
          metricObject[`${idActiveSector}`] = { ...obj }
        }

        const isEqual = _.isEqual(metricObject[`${idActiveSector}`], obj)
        const noChangesAfterCalculating =
          (sector.isCalculated && sector.currentStep !== stepNumbers.result && isEqual) ||
          (sector.currentStep === stepNumbers.result && isEqual)

        if (noChangesAfterCalculating) {
          return
        }

        try {
          await sendMetrics(JSON.stringify(...data))
          metricObject[`${idActiveSector}`] = { ...obj, stepNumber: null }
        } catch (e) {}
      }
    }
  })

  return Promise.resolve({
    ...plugins,
    store
  })
}
